import { Component, OnInit, Renderer2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LanguageServiceService } from './services/language-service.service';
import { DeviceDetectorService } from './services/device-detector.service';
import { LoginService } from './components/Authentication/auth/login.service';
import { WarehouseTypeService } from './services/warehouse-type';
import { WarehouseType } from './enum/warehouse-type.enum';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})

export class AppComponent implements OnInit {
  isMobile: boolean = false;
  isAr: boolean = false;
  sidebarClosed = false;
  installPopupOpen = true;
  deferredPrompt: any;
  showInstallButton = false;
  token!: string | null;
  tokenExists!: boolean;
  warehouseName$ = this.warehouseService.warehouseName$;
  warehouseImage$ = this.warehouseService.warehouseImage$;
  warehouseType$ = this.warehouseService.warehouseType$;
  currentRouteUrl = '';

  private readonly sidebarStateKey = 'blueboxSidebarClosed';
  private arabicStyleId = 'arabic-style';
  constructor(private _languageService: LanguageServiceService,
    private deviceService: DeviceDetectorService,
    private renderer: Renderer2,
    public warehouseService: WarehouseTypeService,
    private _LoginService: LoginService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') == 'ar';
    this.sidebarClosed = localStorage.getItem(this.sidebarStateKey) === 'true';

    this.token = localStorage.getItem('token');
    this.checkForToken();
    this._LoginService.initializeStoredSession().subscribe();
    this.currentRouteUrl =
      window.location.pathname + window.location.search + window.location.hash ||
      this.router.url;
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentRouteUrl = event.urlAfterRedirects;
      });
    this._languageService.currentDirection$.subscribe((direction) => {
      const currentLanguage = localStorage.getItem('language') || 'en';
      document.documentElement.dir = direction;
      document.documentElement.lang = currentLanguage === 'ar' ? 'ar-EG' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US';
      if (direction === 'rtl') {
        this.addArabicStylesheet();
      } else {
        this.removeArabicStylesheet();
      }
    });
    this.checkSession();
    window.addEventListener('beforeinstallprompt', (event: any) => {
      this.deferredPrompt = event;
      this.showInstallButton = true;
    });

    window.addEventListener('bluebox-sidebar-open-requested', () => {
      this.openSidebar();
    });
  }
  closeInstallPopup(): void {
  this.installPopupOpen = false;
}

  get isPublicClinicPage(): boolean {
    return /^\/\d+(?:[?#].*)?$/.test(this.currentRouteUrl);
  }

  openInstallPopup(): void {
  this.installPopupOpen = true;
}

  toggleSidebar(): void {
    this.sidebarClosed = !this.sidebarClosed;
    localStorage.setItem(this.sidebarStateKey, String(this.sidebarClosed));
  }

  openSidebar(): void {
    this.sidebarClosed = false;
    localStorage.setItem(this.sidebarStateKey, 'false');
  }

  warehouseTypeLabel(type: WarehouseType | null): string {
    switch (type) {
      case WarehouseType.PetCompany:
        return 'company';
      case WarehouseType.Warehouse:
        return 'warehouse';
      case WarehouseType.Shop:
        return 'store';
      case WarehouseType.Clinic:
        return 'clinic';
      case WarehouseType.ShopAndClinic:
        return 'store and clinic';
      case WarehouseType.Shipping:
        return 'shipping';
      default:
        return 'unknown';
    }
  }

  warehouseTypeIcon(type: WarehouseType | null): string {
    switch (type) {
      case WarehouseType.PetCompany:
        return 'fa-solid fa-building';
      case WarehouseType.Warehouse:
        return 'fa-solid fa-warehouse';
      case WarehouseType.Shop:
        return 'fa-solid fa-store';
      case WarehouseType.Clinic:
        return 'fa-solid fa-calendar-days';
      case WarehouseType.ShopAndClinic:
        return 'fa-solid fa-house-medical';
      case WarehouseType.Shipping:
        return 'fa-solid fa-truck-fast';
      default:
        return 'fa-solid fa-circle-question';
    }
  }

  warehouseTypeBadgeClass(type: WarehouseType | null): string {
    switch (type) {
      case WarehouseType.PetCompany:
      case WarehouseType.Shipping:
        return 'bb-badge-soft-blue';
      case WarehouseType.Warehouse:
        return 'bb-badge-soft-cyan';
      case WarehouseType.Shop:
        return 'bb-badge-primary';
      case WarehouseType.Clinic:
        return 'bb-badge-success';
      case WarehouseType.ShopAndClinic:
        return 'bb-badge-warning';
      default:
        return 'bb-badge-danger';
    }
  }

  checkForToken() {
    this._LoginService.token$.subscribe((token) => {
      // Update the tokenExists flag based on the token presence
      this.tokenExists = token != null;
    });
  }
  installPWA(): void {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the A2HS prompt');
        } else {
          console.log('User dismissed the A2HS prompt');
        }
        this.deferredPrompt = null;
        this.showInstallButton = false;
      });
    }
  }
  private addArabicStylesheet(): void {
    if (!this.document.getElementById(this.arabicStyleId)) {
      const linkEl = this.renderer.createElement('link');
      linkEl.id = this.arabicStyleId;
      linkEl.rel = 'stylesheet';
      linkEl.href = '/assets/styles-ar.css'; // ✅ COMPILED CSS only
      this.renderer.appendChild(this.document.head, linkEl);
    }
  }

  private removeArabicStylesheet(): void {
    const existingLink = this.document.getElementById(this.arabicStyleId);
    if (existingLink) {
      this.renderer.removeChild(this.document.head, existingLink);
    }
  }
  changeLanguage(language: string) {
    this._languageService.changeLanguage(language);
  }

  showPopup: boolean = false;

  openPopup() {
    this.showPopup = true;
  }

  closePopup() {
    this.showPopup = false;
  }

  setCookie(c_name: any, value: any, exdays: any) {
    var exdate = new Date();
    exdate.setDate(exdate.getDate() + exdays);
    var c_value = escape(value) + ((exdays == null) ? "" : "; expires=" + exdate.toUTCString());
    document.cookie = c_name + "=" + c_value;
  }

  getCookie(c_name: any) {
    var c_value = document.cookie;
    var c_start = c_value.indexOf(" " + c_name + "=");
    if (c_start == -1) { c_start = c_value.indexOf(c_name + "="); } if (c_start == -1) { c_value = ''; }
    else {
      c_start = c_value.indexOf("=", c_start) + 1;
      var c_end = c_value.indexOf(";", c_start);
      if (c_end == -1) { c_end = c_value.length; }
      c_value = unescape(c_value.substring(c_start, c_end));
    }
    return c_value;
  }



  checkSession() {
    var c = this.getCookie("visited");
    if (c != "yes")
      this.showPopup = true;

    this.setCookie("visited", "yes", null);
  }

}
